import { ReactNode, useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, TextField } from '@mui/material';

export type DialogAction = 'ok' | 'cancel';

interface NoteConfig {
  enabled?: boolean;
  required?: boolean;
  placeholder?: string;
  label?: string;
  value?: string;
  okLabelAfterReveal?: string;
}

interface ConfirmDialogProps extends DialogProps {
  onAction?: (value: DialogAction, note?: string) => void;
  text?: ReactNode;
  okBtnLabel?: string;
  cancelBtnLabel?: string;
  hideCancelBtn?: boolean;
  noteConfig?: NoteConfig;
}

function ConfirmDialog({ onAction, text, okBtnLabel, cancelBtnLabel, hideCancelBtn, title, noteConfig, ...other }: ConfirmDialogProps) {
  const { enabled = false, required = false, placeholder = 'General Note', value, label, okLabelAfterReveal } = noteConfig || {};

  const [note, setNote] = useState(value || '');
  const [showNoteBox, setShowNoteBox] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleCancel = () => {
    onAction?.('cancel');
  };

  const handleOk = () => {
    if (required && !showNoteBox) {
      setShowNoteBox(true);
      return;
    }

    onAction?.('ok', note.trim() || undefined);
  };

  useEffect(() => {
    setNote(value || '');
  }, [other.open, value]);

  useEffect(() => {
    setShowNoteBox(false);
  }, [other.open]);

  useEffect(() => {
    if (showNoteBox) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [showNoteBox]);

  const finalOkLabel = showNoteBox ? okLabelAfterReveal || okBtnLabel : okBtnLabel;
  const finalLabel = showNoteBox ? label || text : text;

  return (
    <Dialog sx={{ '& .MuiDialog-paper': { width: '100%', maxWidth: 400, maxHeight: 435 } }} {...other}>
      <DialogTitle>{title ?? 'Confirmation'}</DialogTitle>
      <DialogContent>
        {finalLabel || 'Are you sure you want to Delete?'}

        {enabled && showNoteBox && (
          <TextField
            fullWidth
            multiline
            rows={4}
            onChange={(e) => {
              setNote(e.target.value);
            }}
            value={note}
            sx={{ mt: 1 }}
            placeholder={placeholder}
            inputRef={inputRef}
          />
        )}
      </DialogContent>
      <DialogActions>
        {!hideCancelBtn && (
          <Button color="error" autoFocus onClick={handleCancel}>
            {cancelBtnLabel || 'Cancel'}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleOk}
          sx={{ mr: 0.5, fontWeight: showNoteBox ? 'bold' : 'normal' }}
          disabled={required && showNoteBox && !note.trim()}
        >
          {finalOkLabel || 'Ok'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmDialog;
